import { apiRequest } from "../utils/useAPIRequest";
import { transactionStore } from "../stores";
import constant from "../utils/constant";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "../App";

export const getTransactionsSummary = async (item) => {
  let query;
  if (item) {
    query = item;
  } else {
    query = "";
  }
  const { status, errorMessage, data } = await apiRequest({
    url: `/transactions/transaction-summary?${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    transactionStore.setSummary(data);

    return { status };
  }

  return { status, errorMessage };
};

export const getTransactionsAnalysis = async (query) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/transactions/all-transaction-summary?${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    transactionStore.setTransactionsAnalysis(data);

    return { status };
  }

  return { status, errorMessage };
};

const useGetAllTransactions = (
  transactionTableQuery,
  filterQuery,
  searchQuery
) => {
  // const id = businessStore.bId

  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${transactionTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${transactionTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${transactionTableQuery}&${filterQuery}`;
    } else {
      return transactionTableQuery;
    }
  }, [filterQuery, transactionTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getAllTransactions", query],
    queryFn: () => {
      return apiRequest({
        url: `/transactions?category=WALLET&${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const useGetChargeAnalysis = (
  chargeAnalysisTableQuery,
  filterQuery,
  searchQuery
) => {
  // const id = businessStore.bId

  const query = useMemo(() => {
    if (searchQuery) {
      return `${chargeAnalysisTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${chargeAnalysisTableQuery}&${filterQuery}`;
    } else {
      return chargeAnalysisTableQuery;
    }
  }, [filterQuery, chargeAnalysisTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getChargeAnalysis", query],
    queryFn: () => {
      return apiRequest({
        url: `/charges/all-inflow-charges?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const useGetKudaTransactions = (
  kudaTranTableQuery,
  filterQuery,
  searchQuery
) => {
  // const id = businessStore.bId

  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${kudaTranTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${kudaTranTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${kudaTranTableQuery}&${filterQuery}`;
    } else {
      return kudaTranTableQuery;
    }
  }, [filterQuery, kudaTranTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getKudaTransactions", query],
    queryFn: () => {
      return apiRequest({
        url: `/transactions/kuda-transactions/admin?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};
const useGetProvidusTransactions = (
  providusTranTableQuery,
  filterQuery,
  searchQuery
) => {
  // const id = businessStore.bId

  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${providusTranTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${providusTranTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${providusTranTableQuery}&${filterQuery}`;
    } else {
      return providusTranTableQuery;
    }
  }, [filterQuery, providusTranTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getProvidusTransactions", query],
    queryFn: () => {
      return apiRequest({
        url: `/transactions/providus-transactions/admin?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const updateTransactionStatus = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: "/transactions/update-transfer-status",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getAllTransactions"],
    });
    return { status: constant.Success };
  }
  return { errorMessage, status };
};

const reTriggerTransfer = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: "/transactions/retrigger-transfer",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getAllTransactions"],
    });
    return { status: constant.Success };
  }
  return { errorMessage, status };
};

const reverseTransaction = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: "/transactions/reverse-transfer",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getAllTransactions"],
    });
    return { status: constant.Success };
  }
  return { errorMessage, status };
};

const useGetPaymentMethod = (
  paymentMethodTableQuery,
  filterQuery,
  searchQuery
) => {
  // const id = businessStore.bId

  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${paymentMethodTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${paymentMethodTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${paymentMethodTableQuery}&${filterQuery}`;
    } else {
      return paymentMethodTableQuery;
    }
  }, [filterQuery, paymentMethodTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getPaymentMethod", query],
    queryFn: () => {
      return apiRequest({
        url: `/bank-accounts/payment-all-methods?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const createPaymentMethod = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: "/bank-accounts/admin-create-payment-method",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getPaymentMethod"],
    });
    return { status: constant.Success };
  }
  return { errorMessage, status };
};
const editPaymentMethod = async (payload, id) => {
  const { status, errorMessage } = await apiRequest({
    url: `/bank-accounts/admin-update-payment-method/${id}`,
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getPaymentMethod"],
    });
    return { status: constant.Success };
  }
  return { errorMessage, status };
};

export default {
  getTransactionsSummary,
  reverseTransaction,
  getTransactionsAnalysis,
  useGetAllTransactions,
  useGetChargeAnalysis,
  useGetKudaTransactions,
  useGetProvidusTransactions,
  updateTransactionStatus,
  reTriggerTransfer,
  useGetPaymentMethod,
  createPaymentMethod,
  editPaymentMethod,
};
